import {z as r} from 'zod';

import {deserializeApiError, deserializeV2, UnknownType} from '../../util/api-adapter';
import {fetchBlob, FetchMethodEnum, fetchX} from '../../util/fetch';
import {objectToUrlParameters} from '../../util/url';
import {getCsrfHeaders, rootApiUrl} from '../api/api-const';

export const CUSTOM_REVIEWS_IMPORT_TEMPLATE_FILENAME = 'reviews-import-template.xlsx';

export type ImportCustomReviewsPayloadType = {
    reviewsFile: File;
    brandId: number;
    companyId?: number;
    tagIds?: Array<number>;
};

const importCustomReviewsResponseSchema = r.object({
    createdCount: r.number(),
});

export type ImportCustomReviewsResponseType = r.infer<typeof importCustomReviewsResponseSchema>;

const importCustomReviewsFieldErrorSchema = r.union([r.string(), r.array(r.string())]);

const importCustomReviewsErrorItemSchema = r.object({
    file: importCustomReviewsFieldErrorSchema.optional(),
    brandId: importCustomReviewsFieldErrorSchema.optional(),
    companyId: importCustomReviewsFieldErrorSchema.optional(),
    tagIds: importCustomReviewsFieldErrorSchema.optional(),
    expectedColumns: r.array(r.string()).optional(),
    row: r.union([r.string(), r.number()]).optional(),
    field: r.string().optional(),
    message: r.string().optional(),
});

export type ImportCustomReviewsErrorItemType = r.infer<typeof importCustomReviewsErrorItemSchema>;

const importCustomReviewsErrorSchema = r.object({
    errors: r.array(importCustomReviewsErrorItemSchema).optional(),
    detail: r.string().optional(),
    nonFieldErrors: r.array(r.string()).optional(),
});

type ImportCustomReviewsErrorType = r.infer<typeof importCustomReviewsErrorSchema>;

const importCustomReviewsUrl = `${rootApiUrl}/v4/reviews/custom_reviews/upload/`;
const importCustomReviewsTemplateUrl = `${rootApiUrl}/v4/reviews/custom_reviews/template/`;

export function fetchCustomReviewsImportTemplate(): Promise<Blob | void> {
    return fetchBlob(
        `${importCustomReviewsTemplateUrl}?${objectToUrlParameters({
            timestamp: Date.now(),
        })}`
    );
}

export function parseImportCustomReviewsApiError(error: unknown): Partial<ImportCustomReviewsErrorType> | null {
    return deserializeApiError<ImportCustomReviewsErrorType>(
        importCustomReviewsUrl,
        importCustomReviewsErrorSchema,
        error
    );
}

export async function fetchImportCustomReviews(
    payload: ImportCustomReviewsPayloadType
): Promise<ImportCustomReviewsResponseType> {
    const formData = new FormData();

    formData.append('reviews_file', payload.reviewsFile);
    formData.append('brand_id', String(payload.brandId));

    if (payload.companyId) {
        formData.append('company_id', String(payload.companyId));
    }

    payload.tagIds?.forEach((tagId) => {
        formData.append('tag_ids', String(tagId));
    });

    const response: UnknownType = await fetchX(importCustomReviewsUrl, {
        method: FetchMethodEnum.post,
        headers: {...getCsrfHeaders()},
        body: formData,
    });

    return deserializeV2<ImportCustomReviewsResponseType>(
        importCustomReviewsUrl,
        importCustomReviewsResponseSchema,
        response
    );
}
